/**
 * Whether other GRAutomates tabs are open in this browser.
 *
 * A new app version activates in EVERY open tab at once (the service-worker
 * library reloads each one), so it may only be taken when no other tab could
 * be holding unsaved work. Each tab answers a ping on a BroadcastChannel; the
 * tab's own id keeps it from answering itself. Where the channel does not
 * exist the answer is "assume others are open", the cautious one.
 */

const CHANNEL = 'grautomates-tabs';
const TAB_ID = Math.random().toString(36).slice(2);

/** Answer other tabs' pings for as long as this tab is open. Returns a cleanup. */
export function answerTabPings(): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  const channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = e => {
    const msg = e.data as { type?: string; from?: string } | null;
    if (msg?.type === 'ping' && msg.from !== TAB_ID) channel.postMessage({ type: 'here', to: msg.from });
  };
  return () => channel.close();
}

/** Resolves true if another GRAutomates tab answers within `waitMs`. */
export function otherTabsOpen(waitMs = 300): Promise<boolean> {
  if (typeof BroadcastChannel === 'undefined') return Promise.resolve(true);
  return new Promise(resolve => {
    const channel = new BroadcastChannel(CHANNEL);
    let settled = false;
    const finish = (answer: boolean) => {
      if (settled) return;
      settled = true;
      channel.close();
      resolve(answer);
    };
    channel.onmessage = e => {
      const msg = e.data as { type?: string; to?: string } | null;
      if (msg?.type === 'here' && msg.to === TAB_ID) finish(true);
    };
    channel.postMessage({ type: 'ping', from: TAB_ID });
    setTimeout(() => finish(false), waitMs);
  });
}
